import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, Settings2, Search, Camera, BookOpen,
  Store, Plug,
  KanbanSquare, Webhook, HelpCircle, Mail, BarChart3, Landmark, Radar, Instagram, Linkedin, Youtube, Inbox, Filter, Car,
  TrendingUp, Gauge, MoreHorizontal, MessageSquare, UserCircle2,
  ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight,
  Users, ExternalLink, Wand2, Lock, ShieldCheck, Loader2, Target, RotateCcw,
  Shield, Upload, AtSign, Send, ListChecks, Sparkles, Zap, Ban, BookmarkCheck, GraduationCap,
  GripVertical,
} from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { useTour } from "../tour/TourContext";
import { useAuth } from "../auth/AuthContext";
import { useSidebarState } from "./SidebarContext";
import { isItemGranted } from "../auth/moduleMap";
import CustomMenus from "./CustomMenus";
import SidebarQuickSearch from "./SidebarQuickSearch";
import CreditsChip from "./CreditsChip";
import useSidebarPrefs from "../hooks/useSidebarPrefs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Master/admin emails authorized to view restricted nav items (Activity Log).
const MASTER_EMAILS = new Set(["master@agentelead.pro"]);

// Pinned items — workflow-oriented nested structure (Find > Manage > Optimize):
//   Dashboard · Lead Sourcing (blue) · Sales Pipeline (green) · AI Intelligence (purple)
const PINNED_ITEMS = [
  { id: "agency-dashboard",   labelKey: "sidebar.agency_dashboard",   icon: LayoutDashboard, route: "/dashboard" },
  // Promoted to top-level for discoverability — was previously nested under
  // Sales Pipeline (users reported they couldn't find it).
  { id: "leads-map-top", labelKey: "sidebar.leads_map", icon: Radar, route: "/leads-map", badge: "NEW", iconColor: "#22C55E" },
  {
    id: "sourcing", label: "Lead Sourcing", icon: Target, group: true, iconColor: "#3B82F6",
    children: [
      { id: "prospecting", labelKey: "sidebar.prospecting", icon: Search, route: "/prospecting", counterKey: "prospecting" },
      {
        id: "government", labelKey: "sidebar.gov_group", icon: Landmark, badge: "GOV", group: true, counterKey: "government",
        children: [
          { id: "gov-kanban",   labelKey: "sidebar.gov_kanban",   icon: Landmark, route: "/gov-kanban" },
          { id: "public-works", labelKey: "sidebar.public_works", icon: Landmark, route: "/public-works" },
          { id: "gc-leaderboard", labelKey: "sidebar.gc_leaderboard", icon: Landmark, route: "/gc-leaderboard", badge: "NEW" },
          { id: "product-catalog", labelKey: "sidebar.product_catalog", icon: Landmark, route: "/product-catalog", badge: "NEW" },
          { id: "sunbiz",       labelKey: "sidebar.sunbiz",       icon: Landmark, route: "/sunbiz" },
          { id: "multi-state",  labelKey: "sidebar.multi_state",  icon: Landmark, route: "/multi-state", badge: "FREE" },
          { id: "no-website",   labelKey: "sidebar.no_website",   icon: Landmark, route: "/no-website-leads", badge: "OSM" },
          { id: "yelp",         labelKey: "sidebar.yelp",         icon: Landmark, route: "/yelp", badge: "YELP" },
          { id: "auto-cruise",  labelKey: "sidebar.auto_cruise",  icon: Landmark, route: "/auto-cruise", badge: "AUTO" },
        ],
      },
      {
        id: "b2c", labelKey: "sidebar.b2c_group", icon: Radar, badge: "B2C", group: true, counterKey: "b2c",
        children: [
          { id: "b2c-intent", labelKey: "sidebar.b2c_intent", icon: Radar, route: "/b2c-intent" },
          { id: "b2c-radar",  labelKey: "sidebar.b2c_radar",  icon: Radar, route: "/b2c-radar" },
        ],
      },
      {
        id: "scrapers", labelKey: "sidebar.scrapers", icon: Radar, badge: "NEW", group: true, counterKey: "scrapers",
        children: [
          { id: "instagram", labelKey: "sidebar.instagram", icon: Instagram, route: "/instagram" },
          { id: "linkedin",  labelKey: "sidebar.linkedin",  icon: Linkedin,  route: "/linkedin" },
          { id: "youtube",   labelKey: "sidebar.youtube",   icon: Youtube,   route: "/youtube" },
          { id: "leads-finder", labelKey: "sidebar.leads_finder", icon: Radar, route: "/leads-finder", badge: "NEW" },
        ],
      },
    ],
  },
  {
    id: "pipeline", label: "Sales Pipeline", icon: KanbanSquare, group: true, iconColor: "#22C55E",
    children: [
      { id: "kanban",    labelKey: "sidebar.kanban",    icon: KanbanSquare, route: "/kanban" },
      { id: "leads-map", labelKey: "sidebar.leads_map", icon: Radar,        route: "/leads-map", badge: "NEW" },
    ],
  },
  {
    id: "intelligence", label: "AI Intelligence", icon: Wand2, group: true, iconColor: "#8B5CF6",
    children: [
      { id: "saas-configurator", labelKey: "sidebar.saas_configurator", icon: Settings2, route: "/config" },
      { id: "lead-gap",          labelKey: "sidebar.lead_gap",          icon: Target,    route: "/lead-gap", badge: "NEW" },
    ],
  },
  // Smith Protective — Master-only department. Iter160.
  // Iter227 · Reorganized into collapsible sub-groups (Dashboard always
  // visible; discovery / outreach / pipeline / playbook groups collapse).
  // Sidebar order is now user-customizable via drag-reorder (localStorage).
  {
    id: "smith", label: "Smith Protective", icon: Shield, group: true, iconColor: "#DC2626",
    masterOnly: true, badge: "NEW",
    children: [
      // Iter249 · Further consolidation (approved from Iter248 backlog):
      //   • Kanban removed — Prospects page now hosts a Table/Kanban
      //     view toggle at its header. Route `/smith/kanban` still works.
      //   • Sequences and Blacklist removed — surfaced as tabs inside
      //     `/smith/outreach?view=sequences|blacklist`. Direct routes
      //     stay functional for deep-linking.
      { id: "smith-hq",        label: "Command HQ",     icon: Shield, route: "/smith/hq",        masterOnly: true, badge: "EXEC" },
      { id: "smith-ask",       label: "Ask Bar",        icon: Sparkles, route: "/smith/ask",     masterOnly: true, badge: "AI", iconColor: "#7C3AED" },
      // Iter291 · Vehicle Security moved from AI Intelligence → Smith
      // Protective (sibling of Command HQ / Ask Bar). It's a Smith
      // module (SecureVehicle™) and belongs with the department.
      { id: "vehicle-security", label: "Vehicle Security", icon: Car,   route: "/smith/vehicle-security", masterOnly: true, badge: "NEW" },
      {
        id: "smith-discovery", label: "Discovery", icon: Sparkles, group: true, iconColor: "#8B5CF6",
        masterOnly: true, reorderable: true,
        children: [
          { id: "smith-ai-search",  label: "AI Lead Search", icon: Sparkles, route: "/smith/ai-search", masterOnly: true, badge: "AI" },
          { id: "smith-ai-signals", label: "Signals",        icon: Zap,      route: "/smith/ai-search/signals", masterOnly: true, badge: "AI" },
          { id: "smith-watchlist",  label: "Watchlist",      icon: BookmarkCheck, route: "/smith/watchlist", masterOnly: true, badge: "NEW" },
          { id: "smith-opphub",     label: "Opportunity Hub", icon: Radar,     route: "/smith/opportunity-hub", masterOnly: true, badge: "NEW" },
          { id: "smith-prospects",  label: "Prospects",      icon: Target,   route: "/smith/prospects", masterOnly: true, counterKey: "smith_prospects" },
          { id: "smith-icps",       label: "ICPs",           icon: Users,    route: "/smith/icps",      masterOnly: true, badge: "NEW" },
          { id: "smith-imports",    label: "Imports",        icon: Upload,   route: "/smith/imports",   masterOnly: true, badge: "NEW" },
        ],
      },
      {
        id: "smith-outreach-group", label: "Outreach", icon: Send, group: true, iconColor: "#F59E0B",
        masterOnly: true, reorderable: true,
        children: [
          { id: "smith-outreach",  label: "Outreach",      icon: Send, route: "/smith/outreach", masterOnly: true, badge: "NEW", counterKey: "smith_retry_queue", counterUrgent: true },
        ],
      },
      {
        id: "smith-pipeline-group", label: "Pipeline", icon: KanbanSquare, group: true, iconColor: "#22C55E",
        masterOnly: true, reorderable: true,
        children: [
          { id: "smith-map",    label: "Territory Map", icon: Radar,        route: "/smith/map",    masterOnly: true },
        ],
      },
    ],
  },
];

// Semantic badge palette: amber = urgency (gov/deadlines), purple = AI/Claire,
// blue = system/new features.
const BADGE_STYLES = {
  GOV: "bg-[#F59E0B] text-[#1A1A2E]",
  AUTO: "bg-[#F59E0B] text-[#1A1A2E]",
  B2C: "bg-[#8B5CF6] text-white",
  AI: "bg-[#8B5CF6] text-white",
};
const badgeCls = (b) => BADGE_STYLES[b] || "bg-[#3B82F6] text-white";

// Collect every route nested under an item (for active-state detection).
const collectRoutes = (it) => {
  const out = [];
  if (it.route) out.push(it.route);
  (it.children || []).forEach((c) => out.push(...collectRoutes(c)));
  return out;
};

// Secondary items — collapsed under a "More +" expander.
// Iter99 — `saas-configurator` (Auto Pilot) and `prospecting` (Lead Discovery)
// promoted to PINNED above. Standalone `gov-kanban`, `public-works`, `b2c-intent`,
// `b2c-radar` are also gone (now nested inside the Government / B2C groups).
// Secondary items — collapsed under a "More +" expander, organized into
// nested parent categories (collapsed by default, click to expand).
const MORE_ITEMS = [
  {
    id: "engagement", label: "Engagement", icon: MessageSquare, group: true, iconColor: "#3B82F6",
    children: [
      { id: "campaigns", labelKey: "sidebar.campaigns", icon: Mail,  route: "/campaigns" },
      { id: "replies",   labelKey: "sidebar.replies",   icon: Inbox, route: "/replies", badge: "AI" },
    ],
  },
  {
    id: "analytics", label: "Analytics", icon: BarChart3, group: true, iconColor: "#10B981",
    children: [
      { id: "insights", labelKey: "sidebar.insights", icon: BarChart3,  route: "/insights" },
      { id: "roi",      labelKey: "sidebar.roi",      icon: TrendingUp, route: "/roi", badge: "NEW" },
    ],
  },
  {
    id: "connections", label: "Connections", icon: Plug, group: true, iconColor: "#F59E0B",
    children: [
      { id: "integrations",   labelKey: "sidebar.integrations", icon: Plug,    route: "/integrations" },
      { id: "webhooks",       labelKey: "sidebar.webhooks",     icon: Webhook, route: "/webhooks" },
      { id: "marketing-apis", label: "API Access",              icon: Gauge,   route: "/marketing-apis", badge: "NEW" },
    ],
  },
  {
    id: "resources", label: "Resources", icon: BookOpen, group: true, iconColor: "#A855F7",
    children: [
      { id: "tutorials",       labelKey: "tut.nav.title",           icon: GraduationCap, route: "/tutorials", badge: "NEW" },
      { id: "library",         labelKey: "sidebar.library",         icon: BookOpen, route: "/library" },
      { id: "app-marketplace", labelKey: "sidebar.app_marketplace", icon: Store,    route: "/marketplace" },
    ],
  },
  {
    id: "management", label: "Management", icon: UserCircle2, group: true, iconColor: "#EF4444",
    children: [
      { id: "account-snapshots", labelKey: "sidebar.activity_log", icon: Camera, route: "/log", masterOnly: true },
    ],
  },
];

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLang();
  const { startTour } = useTour();
  const { user } = useAuth();
  const { collapsed, toggle, mobileOpen, setMobileOpen } = useSidebarState();
  // Close drawer on route change (mobile UX)
  useEffect(() => { setMobileOpen(false); }, [location.pathname, setMobileOpen]);
  // Track open/closed state for EACH group independently.
  //   • First render (no persisted state) → auto-open the primary workflow
  //     groups `sourcing` + `pipeline` + `intelligence` so new users can see
  //     every tool without clicking. Also open ancestors of the active route.
  //   • Subsequent renders read/write to localStorage so a returning user's
  //     collapsed choice sticks across sessions.
  const [openGroups, setOpenGroups] = useState(() => {
    let persisted = null;
    try {
      const raw = localStorage.getItem("alp.sidebar.openGroups");
      if (raw) persisted = JSON.parse(raw);
    } catch { /* corrupt storage — ignore */ }
    const initial = {};
    const walk = (items) => {
      items.forEach((it) => {
        if (it.group) {
          const activeAncestor = collectRoutes(it).includes(location.pathname);
          if (persisted && Object.prototype.hasOwnProperty.call(persisted, it.id)) {
            initial[it.id] = !!persisted[it.id] || activeAncestor;
          } else {
            // First-ever visit → open the three primary workflow buckets.
            const firstTimeOpen = ["sourcing", "pipeline", "intelligence"].includes(it.id);
            initial[it.id] = firstTimeOpen || activeAncestor;
          }
          walk(it.children || []);
        }
      });
    };
    walk(PINNED_ITEMS);
    walk(MORE_ITEMS);
    return initial;
  });
  const setGroupOpen = (id, value) =>
    setOpenGroups((prev) => {
      const next = { ...prev, [id]: typeof value === "function" ? value(prev[id]) : value };
      try { localStorage.setItem("alp.sidebar.openGroups", JSON.stringify(next)); } catch { /* quota */ }
      return next;
    });
  // 24h capture counters per sourcing tool (sidebar dynamic badges)
  const [navCounters, setNavCounters] = useState({});
  useEffect(() => {
    axios.get(`${API}/nav-counters`).then(({ data }) => setNavCounters(data || {})).catch(() => {});
    // Iter227 · Smith Prospects counter (works only if user has Smith access)
    axios.get(`${API}/smith/prospects/count`)
      .then(({ data }) => setNavCounters((prev) => ({ ...prev, smith_prospects: data?.total || 0 })))
      .catch(() => {});
    // Iter232 · Retry Queue counter (super admins only) — refreshed every 60s
    const pullRetry = () => axios.get(`${API}/smith/outreach/retry-queue?limit=1`)
      .then(({ data }) => setNavCounters((prev) => ({ ...prev, smith_retry_queue: data?.total || 0 })))
      .catch(() => { /* 403 for non-super-admins, silent */ });
    pullRetry();
    const iv = setInterval(pullRetry, 60_000);
    return () => clearInterval(iv);
  }, []);
  // Iter227 · Smith Protective child ordering (persisted per-user)
  const [smithOrder, setSmithOrder] = useState(() => {
    try {
      const raw = localStorage.getItem("alp.sidebar.smithOrder");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const persistSmithOrder = (order) => {
    setSmithOrder(order);
    try { localStorage.setItem("alp.sidebar.smithOrder", JSON.stringify(order)); } catch { /* quota */ }
  };
  const resetSmithOrder = () => {
    setSmithOrder(null);
    try { localStorage.removeItem("alp.sidebar.smithOrder"); } catch { /* silent */ }
  };
  // Iter249 · Top-level pinned ordering (drag any Smith / Dashboards /
  // Meu App / Discovery entry to reshape the whole sidebar top section).
  const [topOrder, setTopOrder] = useState(() => {
    try {
      const raw = localStorage.getItem("alp.sidebar.topOrder");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const persistTopOrder = (order) => {
    setTopOrder(order);
    try { localStorage.setItem("alp.sidebar.topOrder", JSON.stringify(order)); } catch { /* quota */ }
  };
  const resetTopOrder = () => {
    setTopOrder(null);
    try { localStorage.removeItem("alp.sidebar.topOrder"); } catch { /* silent */ }
  };
  // Iter249 · Generic per-group child ordering (any group, not just Smith).
  const [groupOrders, setGroupOrders] = useState(() => {
    try {
      const raw = localStorage.getItem("alp.sidebar.groupOrders");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const persistGroupOrder = (groupId, order) => {
    setGroupOrders((prev) => {
      const next = { ...(prev || {}), [groupId]: order };
      try { localStorage.setItem("alp.sidebar.groupOrders", JSON.stringify(next)); } catch { /* quota */ }
      return next;
    });
  };
  // Iter282 · MORE-section ordering (bottom of sidebar) so ALL sidebar
  // items — top pinned, group children, and More entries — support
  // drag reorder + Alt+↑/↓ keyboard shortcut. Persisted per-user.
  const [moreOrder, setMoreOrder] = useState(() => {
    try {
      const raw = localStorage.getItem("alp.sidebar.moreOrder");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const persistMoreOrder = (order) => {
    setMoreOrder(order);
    try { localStorage.setItem("alp.sidebar.moreOrder", JSON.stringify(order)); } catch { /* quota */ }
  };
  const resetMoreOrder = () => {
    setMoreOrder(null);
    try { localStorage.removeItem("alp.sidebar.moreOrder"); } catch { /* silent */ }
  };
  // Iter282 · Live drop-position indicator {id, pos:'above'|'below'}
  // shared across siblings so exactly one insertion line renders at a
  // time. Cleared onDragEnd / onDrop.
  const [dragOver, setDragOver] = useState({ id: null, pos: null });
  // "More +" expander: open by default if the current page is one of the secondary items.
  const moreRoutes = MORE_ITEMS.flatMap((m) => collectRoutes(m));
  const [moreOpen, setMoreOpen] = useState(() => moreRoutes.includes(location.pathname));

  // Iter337 · Mirror all order + open-groups state to
  // /api/user/sidebar-prefs so a fresh device/login preserves the
  // layout the user configured. localStorage remains the fast path.
  useSidebarPrefs({
    authToken: user?.token || (typeof window !== "undefined"
                                  ? localStorage.getItem("token") : null),
    topOrder, setTopOrder,
    groupOrders, setGroupOrders,
    smithOrder, setSmithOrder,
    moreOrder, setMoreOrder,
    openGroups, setOpenGroups,
  });

  const isMaster = !!(user && (user.role === "super_admin" || user.role === "admin" || MASTER_EMAILS.has((user.email || "").toLowerCase())));
  const isCliente = !!(user && (user.role === "cliente_admin" || user.role === "cliente_user"));

  // Tenant-aware module gate (Iter48): clientes only see modules the master granted.
  // We accept either an array on the user object OR a separate fetch — but to keep
  // this component simple, we only show items if the moduleId is in user.module_grants
  // when the user is a cliente. Super admins / master see everything.
  const tenantModuleGrants = user?.module_grants || null;
  const isModuleGranted = (it) =>
    isItemGranted(it.id, { isMaster, isCliente, tenantModuleGrants });

  const visiblePinned = PINNED_ITEMS
    .filter((it) => !it.masterOnly || isMaster)
    .filter(isModuleGranted)
    .map((it) => (it.group
      ? { ...it, children: (it.children || []).filter(isModuleGranted) }
      : it))
    .filter((it) => !it.group || it.children.length > 0)
    // Iter227 · Apply persisted Smith Protective child ordering
    // Iter249 · Apply persisted per-group child ordering for any group
    .map((it) => {
      if (!it.group || !Array.isArray(it.children)) return it;
      const savedOrder = it.id === "smith"
        ? smithOrder
        : (groupOrders || {})[it.id];
      if (!savedOrder || !Array.isArray(savedOrder)) return it;
      const byId = new Map(it.children.map((c) => [c.id, c]));
      const ordered = [];
      savedOrder.forEach((cid) => { if (byId.has(cid)) { ordered.push(byId.get(cid)); byId.delete(cid); } });
      byId.forEach((c) => ordered.push(c));
      return { ...it, children: ordered };
    });
  // Iter249 · Apply persisted top-level ordering across pinned items.
  const orderedTopPinned = (() => {
    if (!topOrder || !Array.isArray(topOrder)) return visiblePinned;
    const byId = new Map(visiblePinned.map((it) => [it.id, it]));
    const out = [];
    topOrder.forEach((id) => { if (byId.has(id)) { out.push(byId.get(id)); byId.delete(id); } });
    byId.forEach((it) => out.push(it));
    return out;
  })();
  const visibleMore = MORE_ITEMS
    .filter((it) => !it.masterOnly || isMaster)
    .filter(isModuleGranted)
    .map((it) => (it.group
      ? { ...it, children: (it.children || []).filter((c) => (!c.masterOnly || isMaster) && isModuleGranted(c)) }
      : it))
    .filter((it) => !it.group || it.children.length > 0);
  // Iter282 · Apply persisted More ordering so users can drag secondary
  // entries around too (Marketing APIs, Webhooks, Library, etc.).
  const orderedVisibleMore = (() => {
    if (!moreOrder || !Array.isArray(moreOrder)) return visibleMore;
    const byId = new Map(visibleMore.map((it) => [it.id, it]));
    const out = [];
    moreOrder.forEach((id) => { if (byId.has(id)) { out.push(byId.get(id)); byId.delete(id); } });
    byId.forEach((it) => out.push(it));
    return out;
  })();
  const moreLeafCount = orderedVisibleMore.reduce((n, it) => n + (it.group ? it.children.length : 1), 0);

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          data-testid="sidebar-mobile-backdrop"
          className="lg:hidden fixed inset-0 z-30 bg-black/60 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <aside
        data-testid="sidebar"
        data-collapsed={collapsed ? "true" : "false"}
        style={{ width: "var(--al-sidebar-w, 260px)" }}
        className={
          "al-sidebar-scroll fixed left-0 top-0 bottom-0 bg-[#1A1A2E] text-white flex flex-col z-40 overflow-y-auto overflow-x-hidden transition-transform duration-200 ease-out " +
          (mobileOpen ? "translate-x-0" : "-translate-x-full") +
          " lg:translate-x-0"
        }
      >
      <div className={`border-b border-white/5 ${collapsed ? "px-2 py-4" : "px-4 py-5"}`}>
        <div className={`flex items-center bg-white rounded-xl ${collapsed ? "justify-center p-2" : "gap-3 px-3 py-3"}`}>
          <img
            src="/logos/agentelead-pro-light-bg.png"
            alt={t("sidebar.brand")}
            data-testid="sidebar-logo-icon"
            className={collapsed ? "w-10 h-10 object-contain" : "w-14 h-14 object-contain shrink-0"}
          />
          {!collapsed && (
            <div className="leading-tight">
              <div className="font-bold text-[16px] text-[#1A1A2E]">{t("sidebar.brand")}</div>
              <div className="text-[10px] text-[#6B7280] tracking-[0.18em] font-semibold mt-0.5">{t("sidebar.subtitle")}</div>
            </div>
          )}
        </div>
      </div>

      {/* Collapse toggle */}
      <button
        type="button"
        data-testid="sidebar-toggle"
        onClick={toggle}
        title={collapsed ? t("sidebar.expand") : t("sidebar.collapse")}
        className={`mt-2 mx-3 h-8 flex items-center justify-center rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-colors ${collapsed ? "" : "gap-2 text-[11.5px] font-medium"}`}
      >
        {collapsed
          ? <ChevronsRight className="w-4 h-4" />
          : (<><ChevronsLeft className="w-4 h-4" /> {t("sidebar.collapse")}</>)}
      </button>

      <nav className={`flex-1 py-4 space-y-1 ${collapsed ? "px-2" : "px-3"}`}>
        {!collapsed && <SidebarQuickSearch navigate={navigate} t={t} />}
        {orderedTopPinned.map((it, idx) => renderItem(it, {
          collapsed, location, navigate, t, openGroups, setGroupOpen, navCounters,
          dragOver, setDragOver,
          // Iter249 · Top-level drag reorder — any pinned entry can be
          // reshuffled to reshape the sidebar. Also enables intra-group
          // reorder for every group (previously Smith-only).
          indexInParent: idx,
          reorderParent: {
            parentId: "__root__",
            currentOrder: orderedTopPinned.map((p) => p.id),
            persist: persistTopOrder,
            reset: resetTopOrder,
          },
          // Iter227/249 · Enable drag-reorder inside every group's children.
          childReorder: it.id === "smith" ? {
            parentId: "smith",
            currentOrder: it.children.map((c) => c.id),
            persist: persistSmithOrder,
            reset: resetSmithOrder,
          } : (it.group ? {
            parentId: it.id,
            currentOrder: (it.children || []).map((c) => c.id),
            persist: (order) => persistGroupOrder(it.id, order),
            reset: () => persistGroupOrder(it.id, null),
          } : null),
        }))}

        {/* Custom user menus — folders for saved searches & leads */}
        {!collapsed && <CustomMenus />}

        {/* More + collapsible group */}
        {visibleMore.length > 0 && (
          <div className="pt-2 mt-2 border-t border-white/5">
            <button
              type="button"
              data-testid="sidebar-more-toggle"
              onClick={() => {
                if (collapsed) return; // toggle disabled in collapsed mode
                setMoreOpen((v) => !v);
              }}
              title={collapsed ? t("sidebar.more") : undefined}
              className={
                "w-full flex items-center rounded-lg text-[13px] transition-all " +
                (collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5 ") +
                "text-white/60 hover:bg-white/5 hover:text-white"
              }
            >
              <MoreHorizontal className="w-[18px] h-[18px] shrink-0" />
              {!collapsed && (
                <>
                  <span className="flex-1 text-left">{t("sidebar.more")}</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/10 text-white/70">
                    {moreLeafCount}
                  </span>
                  {moreOpen ? <ChevronDown className="w-3.5 h-3.5 opacity-70" /> : <ChevronRight className="w-3.5 h-3.5 opacity-70" />}
                </>
              )}
            </button>
            {!collapsed && moreOpen && (
              <div className="ml-2 mt-0.5 border-l border-white/10 pl-2 space-y-0.5"
                   data-testid="sidebar-more-list">
                {orderedVisibleMore.map((it, idx) => renderItem(it, {
                  collapsed, location, navigate, t, openGroups, setGroupOpen,
                  smaller: true,
                  dragOver, setDragOver,
                  // Iter282 · MORE entries are now drag-reorderable too.
                  indexInParent: idx,
                  reorderParent: {
                    parentId: "__more__",
                    currentOrder: orderedVisibleMore.map((p) => p.id),
                    persist: persistMoreOrder,
                    reset: resetMoreOrder,
                  },
                }))}
                {/* Restore default expanded layout (clears the persisted
                    collapse-state so first-render defaults kick in again). */}
                <button
                  type="button"
                  data-testid="sidebar-reset-layout"
                  onClick={() => {
                    try { localStorage.removeItem("alp.sidebar.openGroups"); } catch { /* quota */ }
                    // Iter282 · reset every persisted order layer.
                    try {
                      localStorage.removeItem("alp.sidebar.topOrder");
                      localStorage.removeItem("alp.sidebar.smithOrder");
                      localStorage.removeItem("alp.sidebar.groupOrders");
                      localStorage.removeItem("alp.sidebar.moreOrder");
                    } catch { /* quota */ }
                    setTopOrder(null);
                    setSmithOrder(null);
                    setGroupOrders({});
                    setMoreOrder(null);
                    setOpenGroups(() => {
                      const next = {};
                      const walk = (items) => items.forEach((it) => {
                        if (it.group) {
                          next[it.id] = ["sourcing", "pipeline", "intelligence"].includes(it.id)
                            || collectRoutes(it).includes(location.pathname);
                          walk(it.children || []);
                        }
                      });
                      walk(PINNED_ITEMS); walk(MORE_ITEMS);
                      return next;
                    });
                    toast.success(t("sidebar.reset_done") || "Menu restaurado");
                  }}
                  className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md text-[11.5px] text-white/50 hover:text-white hover:bg-white/5 transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  {t("sidebar.reset_layout") || "Restaurar layout do menu"}
                </button>
              </div>
            )}
            {/* In collapsed mode, surface secondary items as compact icons under the "More" pill */}
            {collapsed && (
              <div className="mt-1 space-y-0.5">
                {orderedVisibleMore.map((it) => renderItem(it, {
                  collapsed, location, navigate, t, openGroups, setGroupOpen,
                }))}
              </div>
            )}
          </div>
        )}
      </nav>

      <CreditsChip collapsed={collapsed} />

      <div className={`pt-2 pb-3 border-t border-white/5 ${collapsed ? "px-2" : "px-3"}`}>
        {!collapsed && user && user.email && (
          <div
            data-testid="sidebar-user-info"
            className="px-3 py-2 mb-2 bg-white/5 rounded-lg flex items-center gap-2"
          >
            <div className="w-7 h-7 rounded-full bg-[#C9A24E] text-[#0A1233] flex items-center justify-center text-[11px] font-bold shrink-0">
              {(user.name || user.email).slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 leading-tight">
              <div className="text-[11.5px] font-semibold text-white truncate">{user.name || user.email.split("@")[0]}</div>
              <div className="text-[10px] text-white/50 truncate">{user.email}</div>
            </div>
          </div>
        )}
        {!collapsed && isMaster && <SidebarPersonaSwitcher />}
        <button
          type="button"
          data-testid="sidebar-tour-restart"
          onClick={startTour}
          title={collapsed ? t("tour.restart") : undefined}
          className={
            "w-full flex items-center rounded-lg text-[12.5px] text-white/70 hover:bg-white/5 hover:text-white transition-all " +
            (collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5")
          }
        >
          <HelpCircle className="w-[16px] h-[16px] shrink-0" />
          {!collapsed && <span className="flex-1 text-left">{t("tour.restart")}</span>}
        </button>
        {!collapsed && (
          <div className="px-2 pt-2 text-[11px] text-white/40">
            {t("sidebar.footer")}
          </div>
        )}
      </div>
    </aside>
    </>
  );
}

// SidebarPersonaSwitcher — collapsible quick-switcher for Master users.
// Renders inside Sidebar so it's available on EVERY page, not just Master Console.
function SidebarPersonaSwitcher() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [personas, setPersonas] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [err, setErr] = useState("");
  const [errStatus, setErrStatus] = useState(0);
  const [whoami, setWhoami] = useState(null);

  const fetchPersonas = async () => {
    setLoading(true); setErr(""); setErrStatus(0);
    try {
      const { data } = await axios.get(`${API}/master/demo-accounts`);
      setPersonas(data.accounts || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load");
      setErrStatus(e?.response?.status || 0);
      setPersonas([]);
      // On 403 we ALSO fetch /me so the dropdown can show the actual role
      // the backend sees — invaluable for diagnosing role-drift bugs in
      // deployed environments where the master can't tail logs.
      if (e?.response?.status === 403) {
        try {
          const { data: meResp } = await axios.get(`${API}/auth/me`);
          // /auth/me returns { user: { role, email, ... } } — unwrap so the
          // diagnostic panel reads the right shape.
          setWhoami(meResp?.user || meResp);
        } catch (_e) { /* swallow */ }
      }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open || personas !== null) return;
    fetchPersonas();
     
  }, [open, personas]);

  const seedAccounts = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/master/demo-accounts/seed?reset_passwords=false`);
      toast.success(t("sidebar.demo_seeded_toast"));
      setPersonas(null); // force re-fetch
      await fetchPersonas();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSeeding(false); }
  };

  const impersonate = async (tenantId, label, sameTab = false) => {
    try {
      const { data } = await axios.post(`${API}/master/tenants/${tenantId}/impersonate`);
      if (!data?.access_token) { toast.error(t("sidebar.no_token_toast")); return; }
      const url = `/impersonate?token=${encodeURIComponent(data.access_token)}&dest=/dashboard${sameTab ? "&backup=1" : ""}`;
      if (sameTab) { window.location.href = url; }
      else { window.open(url, "_blank", "noopener"); toast.success(`Opened as ${label}`); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const copyMagic = async (slug, label) => {
    try {
      const { data } = await axios.post(`${API}/auth/magic-link?slug=${slug}&ttl_days=14`);
      await navigator.clipboard.writeText(data.url);
      toast.success(`Magic link · ${label} copied`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  // Always show the REAL count (or "—" while still loading) so the badge
  // never lies about empty / failed states. Previously hard-coded "5".
  const count = personas === null ? "—" : String(personas.length);

  return (
    <div className="mb-2" data-testid="sidebar-persona-switcher">
      <button
        type="button"
        data-testid="sidebar-persona-trigger"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-[#C9A24E]/20 text-[11.5px] text-white"
      >
        <Users className="w-3.5 h-3.5 text-[#C9A24E] shrink-0" />
        <span className="font-bold flex-1 text-left">{t("sidebar.switch_persona")}</span>
        <span data-testid="sidebar-persona-count" className="text-[9px] text-white/45 tabular-nums">{count}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1.5 max-h-[280px] overflow-y-auto rounded-lg border border-white/10 bg-black/30">
          {loading ? (
            <div className="py-3 text-center text-[11px] text-white/55 inline-flex items-center justify-center gap-2 w-full">
              <Loader2 className="w-3 h-3 animate-spin" /> {t("sidebar.loading")}
            </div>
          ) : err ? (
            <div data-testid="sidebar-persona-error" className="px-2.5 py-2.5 text-[10.5px] text-rose-300">
              <div className="font-bold mb-1">{t("sidebar.personas_failed")}</div>
              <div className="text-rose-200/80 mb-1.5 break-words">
                {errStatus ? `[${errStatus}] ` : ""}{err}
              </div>
              {whoami && (
                <div className="text-[9.5px] bg-rose-500/10 rounded p-1.5 mb-1.5 font-mono">
                  {t("sidebar.logged_as")} <span className="text-amber-300">{whoami.email}</span><br/>
                  {t("sidebar.role")} <span className="text-amber-300">{whoami.role || "(none)"}</span>
                  {!["super_admin","admin","master"].includes((whoami.role||"").toLowerCase()) && (
                    <div className="text-rose-300 mt-1 not-italic">
                      {t("sidebar.role_not_admin")}
                    </div>
                  )}
                </div>
              )}
              <button onClick={fetchPersonas}
                className="px-2 py-0.5 rounded border border-rose-400/40 hover:bg-rose-500/10 font-bold">
                {t("sidebar.retry")}
              </button>
            </div>
          ) : (personas || []).length === 0 ? (
            <div data-testid="sidebar-persona-empty" className="px-2.5 py-2.5 text-[10.5px] text-white/55 space-y-2">
              <div>{t("sidebar.demo_seed_prompt")}</div>
              <button onClick={seedAccounts} disabled={seeding}
                      data-testid="sidebar-persona-seed"
                      className="w-full h-7 rounded bg-[#C9A24E] hover:bg-[#dcb670] text-[#1A0E36] text-[10.5px] font-bold inline-flex items-center justify-center gap-1 disabled:opacity-60">
                {seeding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                Seed 5 demo accounts
              </button>
              <div className="text-[9.5px] text-white/45">
                Or visit{" "}
                <a href="/master/demo-accounts" className="text-[#C9A24E] font-bold">{t("sidebar.demo_profiles")}</a>.
              </div>
            </div>
          ) : personas.map((a) => {
            const isTrial = a.package === "personalizado";
            return (
              <div key={a.tenant_id}
                   data-testid={`sidebar-persona-${a.slug}`}
                   className="px-2 py-2 hover:bg-white/[0.05] border-b border-white/5 last:border-b-0">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className={`w-4 h-4 rounded flex items-center justify-center shrink-0 ${isTrial ? "bg-rose-500/20 text-rose-300" : "bg-emerald-500/20 text-emerald-300"}`}>
                    {isTrial ? <Lock className="w-2 h-2" /> : <ShieldCheck className="w-2 h-2" />}
                  </div>
                  <div className="text-[10.5px] font-bold text-white truncate flex-1">{a.label.replace("User ", "U")}</div>
                  {a.actions_24h_total > 0 && (
                    <span className="text-[8.5px] font-bold px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-200">
                      ✓ {a.actions_24h_total}
                    </span>
                  )}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => impersonate(a.tenant_id, a.label)}
                          data-testid={`sidebar-persona-open-${a.slug}`}
                          className="flex-1 h-5 rounded bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-[9.5px] font-bold inline-flex items-center justify-center gap-0.5">
                    <ExternalLink className="w-2 h-2" /> Open
                  </button>
                  <button onClick={() => copyMagic(a.slug, a.label)}
                          data-testid={`sidebar-persona-magic-${a.slug}`}
                          className="flex-1 h-5 rounded bg-[#F59E0B] hover:bg-[#D97706] text-[#1A0E36] text-[9.5px] font-bold inline-flex items-center justify-center gap-0.5">
                    <Wand2 className="w-2 h-2" /> Magic
                  </button>
                </div>
              </div>
            );
          })}
          <a href="/master/demo-accounts"
             className="block px-2 py-1.5 text-[10px] text-[#C9A24E] hover:text-[#dcb670] font-bold text-center border-t border-white/5">
            {t("sidebar.demo_manage")}
          </a>
        </div>
      )}
    </div>
  );
}

// Renders a single sidebar entry. Handles flat routes and nested collapsible
// groups (recursive). Parent categories get colored icons; badges use the
// semantic palette; counterKey shows the 24h capture count.
// Iter227 · When ctx.reorderParent is present AND the item is a direct child
// of that parent (indexInParent given), enables HTML5 drag-reorder.
function renderItem(it, ctx) {
  const { collapsed, location, navigate, t, openGroups, setGroupOpen, smaller,
          navCounters, reorderParent, indexInParent, childReorder,
          dragOver, setDragOver } = ctx;
  const Icon = it.icon;
  const label = it.label || t(it.labelKey);
  const sizeCls = smaller ? "text-[12.5px] py-2" : "text-[13.5px] py-2.5";
  const counter = it.counterKey && navCounters ? navCounters[it.counterKey] || 0 : 0;

  // Iter227/282 · Drag reorder handlers with live drop-position indicator.
  // Reorderable only when a reorderParent + indexInParent are provided
  // and the sidebar is NOT collapsed (no reorder in icon-only mode).
  const reorderable = reorderParent && typeof indexInParent === "number" && !collapsed;

  // Iter282 · Compute the drop position from mouse Y inside the item.
  // If cursor is in the top half → 'above'; bottom half → 'below'.
  const computeDropPos = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return e.clientY - rect.top < rect.height / 2 ? "above" : "below";
  };

  // Iter282 · Alt+ArrowUp / Alt+ArrowDown shifts the focused item within
  // its parent. Accessible keyboard shortcut for reorder.
  const shiftBy = (delta) => {
    if (!reorderable) return;
    const cur = [...(reorderParent.currentOrder || [])];
    const from = cur.indexOf(it.id);
    if (from === -1) return;
    const to = Math.max(0, Math.min(cur.length - 1, from + delta));
    if (to === from) return;
    const next = [...cur];
    next.splice(from, 1);
    next.splice(to, 0, it.id);
    const previous = cur;
    reorderParent.persist(next);
    try {
      toast.success("Menu reordenado", {
        duration: 5000,
        action: { label: "Desfazer", onClick: () => reorderParent.persist(previous) },
      });
    } catch { /* silent */ }
  };

  const dragHandlers = reorderable ? {
    draggable: true,
    onDragStart: (e) => {
      e.stopPropagation();
      e.dataTransfer.setData("text/plain", it.id);
      // Iter282 · scope dnd to the same parent so a Top-level drag
      // doesn't drop inside a More list (mixed contexts).
      e.dataTransfer.setData("application/x-alp-parent", reorderParent.parentId);
      e.dataTransfer.effectAllowed = "move";
      // Iter338 · Custom translucent ghost following the cursor. Clone
      // the current item, apply an opacity/shadow, and give it to the
      // native DataTransfer API (Chrome + Safari both honour setDragImage).
      try {
        const rect = e.currentTarget.getBoundingClientRect();
        const ghost = e.currentTarget.cloneNode(true);
        ghost.setAttribute("data-testid", `sidebar-drag-ghost-${it.id}`);
        ghost.style.position = "absolute";
        ghost.style.top = "-9999px";  // offscreen until browser hands it
        ghost.style.left = "-9999px";
        ghost.style.width = `${rect.width}px`;
        ghost.style.opacity = "0.85";
        ghost.style.background = "var(--al-card-bg, #FFFFFF)";
        ghost.style.border = "1px solid #C9A24E";
        ghost.style.boxShadow = "0 12px 28px rgba(11, 18, 51, 0.28), 0 4px 10px rgba(11, 18, 51, 0.18)";
        ghost.style.borderRadius = "10px";
        ghost.style.transform = "scale(1.02)";
        ghost.style.pointerEvents = "none";
        document.body.appendChild(ghost);
        e.dataTransfer.setDragImage(ghost, e.clientX - rect.left, e.clientY - rect.top);
        // Clean-up on next tick — the browser has already snapshotted.
        setTimeout(() => { try { ghost.remove(); } catch { /* silent */ } }, 60);
      } catch { /* silent if DataTransfer API disallowed */ }
      try { e.currentTarget.style.opacity = "0.4"; } catch { /* silent */ }
    },
    onDragEnd: (e) => {
      try { e.currentTarget.style.opacity = ""; } catch { /* silent */ }
      if (setDragOver) setDragOver({ id: null, pos: null });
    },
    onDragOver: (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      const pos = computeDropPos(e);
      if (setDragOver && (!dragOver || dragOver.id !== it.id || dragOver.pos !== pos)) {
        setDragOver({ id: it.id, pos });
      }
    },
    onDragLeave: () => {
      if (setDragOver && dragOver && dragOver.id === it.id) setDragOver({ id: null, pos: null });
    },
    onDrop: (e) => {
      e.preventDefault(); e.stopPropagation();
      const draggedId = e.dataTransfer.getData("text/plain");
      const draggedParent = e.dataTransfer.getData("application/x-alp-parent");
      if (setDragOver) setDragOver({ id: null, pos: null });
      if (!draggedId || draggedId === it.id) return;
      // Reject cross-parent drops — user is dragging out of their scope.
      if (draggedParent && draggedParent !== reorderParent.parentId) return;
      const cur = [...(reorderParent.currentOrder || [])];
      const fromIdx = cur.indexOf(draggedId);
      const toIdxRaw = cur.indexOf(it.id);
      if (fromIdx === -1 || toIdxRaw === -1) return;
      const pos = computeDropPos(e);
      const next = [...cur];
      next.splice(fromIdx, 1);
      // Adjust for removed element when target index shifts
      let insertAt = next.indexOf(it.id);
      if (pos === "below") insertAt += 1;
      next.splice(insertAt, 0, draggedId);
      // Iter338 · Undo toast — capture the previous order and offer a
      // one-tap revert. Sonner already imported at the top of the file.
      const previous = cur;
      reorderParent.persist(next);
      try {
        toast.success("Menu reordenado", {
          duration: 5000,
          action: {
            label: "Desfazer",
            onClick: () => reorderParent.persist(previous),
          },
        });
      } catch { /* silent */ }
    },
    onKeyDown: (e) => {
      if (e.altKey && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
        e.preventDefault();
        shiftBy(e.key === "ArrowUp" ? -1 : 1);
      }
    },
  } : {};

  // Iter282 · Drop-line indicator — a 2px lime bar drawn above/below the
  // hovered target so users see exactly where the drop will land.
  const showDropLine = reorderable && dragOver && dragOver.id === it.id;
  const dropLineTop = showDropLine && dragOver.pos === "above";
  const dropLineBottom = showDropLine && dragOver.pos === "below";

  if (it.group && it.children) {
    const childActive = collectRoutes(it).includes(location.pathname);
    const open = !collapsed && (!!openGroups?.[it.id] || childActive);
    const firstRoute = collectRoutes(it)[0];
    return (
      <div key={it.id}
           data-testid={reorderable ? `sidebar-reorder-slot-${it.id}` : undefined}
           className={"relative group/reorder transition-all " + (showDropLine ? "" : "")}
           {...dragHandlers}>
        {dropLineTop && (
          <span data-testid={`sidebar-drop-line-above-${it.id}`}
            className="absolute -top-0.5 left-1 right-1 h-0.5 rounded-full pointer-events-none z-10"
            style={{ background: "#84CC16", boxShadow: "0 0 10px #84CC16" }} />
        )}
        {dropLineBottom && (
          <span data-testid={`sidebar-drop-line-below-${it.id}`}
            className="absolute -bottom-0.5 left-1 right-1 h-0.5 rounded-full pointer-events-none z-10"
            style={{ background: "#84CC16", boxShadow: "0 0 10px #84CC16" }} />
        )}
        {reorderable && !collapsed && (
          <span data-testid={`sidebar-drag-handle-${it.id}`}
            className="absolute left-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/reorder:opacity-60 transition-opacity pointer-events-none z-10"
            title="Arraste para reordenar">
            <GripVertical className="w-3 h-3 text-current" />
          </span>
        )}
        <button
          data-testid={`sidebar-item-${it.id}`}
          onClick={() => {
            if (collapsed) {
              if (firstRoute) navigate(firstRoute);
            } else {
              setGroupOpen(it.id, (v) => !v);
            }
          }}
          title={collapsed ? label : (reorderable ? `${label} · arraste ou Alt+↑/↓ para reordenar` : undefined)}
          className={
            `w-full flex items-center rounded-lg ${sizeCls} transition-all ` +
            (collapsed ? "justify-center px-0" : "gap-3 px-3 ") +
            (childActive ? "al-pill-active font-semibold" : "text-white/75 hover:bg-white/5 hover:text-white")
          }
        >
          <Icon className="w-[18px] h-[18px] shrink-0"
            style={it.iconColor && !childActive ? { color: it.iconColor } : undefined} />
          {!collapsed && (
            <>
              <span className="flex-1 text-left">{label}</span>
              {counter > 0 && (
                <span data-testid={`sidebar-counter-${it.id}`}
                  className="text-[9.5px] font-bold px-1.5 py-0.5 rounded-full bg-[#3B82F6]/25 text-[#93C5FD] tabular-nums"
                  title={t("sidebar.new_24h")}>
                  +{counter}
                </span>
              )}
              {it.badge && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${badgeCls(it.badge)}`}>{it.badge}</span>
              )}
              {open ? <ChevronDown className="w-3.5 h-3.5 opacity-70" /> : <ChevronRight className="w-3.5 h-3.5 opacity-70" />}
            </>
          )}
        </button>
        {open && (
          <div className="ml-4 mt-0.5 border-l border-white/10 pl-2 space-y-0.5">
            {it.children.map((child, idx) => renderItem(child, {
              ...ctx, smaller: true,
              // Iter227/249 · Propagate reorder context ONLY at depth-1.
              // Uses `childReorder` (this group's config) when set, else
              // falls back to `reorderParent` for backwards-compat.
              indexInParent: idx,
              reorderParent: childReorder
                || (reorderParent && it.id === reorderParent.parentId ? reorderParent : null),
              childReorder: null,
            }))}
          </div>
        )}
      </div>
    );
  }
  const active = it.route ? location.pathname === it.route : false;
  return (
    <div key={it.id}
         data-testid={reorderable ? `sidebar-reorder-slot-${it.id}` : undefined}
         className="relative group/reorder transition-all"
         {...dragHandlers}>
      {dropLineTop && (
        <span data-testid={`sidebar-drop-line-above-${it.id}`}
          className="absolute -top-0.5 left-1 right-1 h-0.5 rounded-full pointer-events-none z-10"
          style={{ background: "#84CC16", boxShadow: "0 0 10px #84CC16" }} />
      )}
      {dropLineBottom && (
        <span data-testid={`sidebar-drop-line-below-${it.id}`}
          className="absolute -bottom-0.5 left-1 right-1 h-0.5 rounded-full pointer-events-none z-10"
          style={{ background: "#84CC16", boxShadow: "0 0 10px #84CC16" }} />
      )}
      {reorderable && !collapsed && (
        <span data-testid={`sidebar-drag-handle-${it.id}`}
          className="absolute left-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/reorder:opacity-60 transition-opacity pointer-events-none z-10"
          title="Arraste para reordenar">
          <GripVertical className="w-3 h-3 text-current" />
        </span>
      )}
      <button
        data-testid={`sidebar-item-${it.id}`}
        onClick={() => it.route && navigate(it.route)}
        title={collapsed ? label : (reorderable ? `${label} · arraste ou Alt+↑/↓ para reordenar` : undefined)}
        className={
          `w-full flex items-center rounded-lg ${sizeCls} transition-all ` +
          (collapsed ? "justify-center px-0" : "gap-3 px-3 ") +
          (active
            ? "al-pill-active font-semibold"
            : "text-white/75 hover:bg-white/5 hover:text-white")
        }
      >
        <Icon className="w-[18px] h-[18px] shrink-0"
          style={it.iconColor && !active ? { color: it.iconColor } : undefined} />
        {!collapsed && (
          <>
            <span className="flex-1 text-left">{label}</span>
            {counter > 0 && (
              <span data-testid={`sidebar-counter-${it.id}`}
                className={"text-[9.5px] font-bold px-1.5 py-0.5 rounded-full tabular-nums " +
                  (it.counterUrgent
                    ? "bg-red-500/40 text-red-50 ring-1 ring-red-400/70 animate-pulse"
                    : "bg-[#3B82F6]/25 text-[#93C5FD]")}
                title={it.counterKey === "smith_prospects"
                  ? `${counter} prospects disponíveis`
                  : it.counterKey === "smith_retry_queue"
                    ? `${counter} envio(s) falhos aguardando retry`
                    : "Novos nas últimas 24h"}>
                {(it.counterKey === "smith_prospects" || it.counterKey === "smith_retry_queue") ? counter : `+${counter}`}
              </span>
            )}
            {it.badge && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${badgeCls(it.badge)}`}>
                {it.badge}
              </span>
            )}
          </>
        )}
      </button>
    </div>
  );
}
